// Kept mock drafts (MOCK_DRAFT.md §6.1): my list in a league, one mock whole,
// and the three writes — keep a finished one, share one, delete one.
//
// Each write ends with the league's reads asked again (`thenRefresh`), the
// signal every league read listens to, so the list on the mock page and a
// card in the chat follow without a reload.

import { SavedMock, SavedMockSummary, SavedMocksView, type SaveMockRequest } from '@fsp/domain';
import { send, type Reply } from './api';
import { thenRefresh, useMovesVersion } from './moves';
import { useRead, type Read } from './read';

const base = (leagueId: string) => `/leagues/${encodeURIComponent(leagueId)}/draft/mocks`;

/** My kept mocks in a league, newest first. */
export function useSavedMocks(leagueId: string | null): Read<SavedMocksView | null> {
  return useRead(leagueId === null ? null : base(leagueId), SavedMocksView, {
    refresh: useMovesVersion(leagueId),
  });
}

/** One kept mock: mine, or one a leaguemate shared. */
export function useSavedMock(
  leagueId: string | null,
  mockId: string | null,
): Read<SavedMock | null> {
  return useRead(
    leagueId === null || mockId === null ? null : `${base(leagueId)}/${encodeURIComponent(mockId)}`,
    SavedMock,
    { refresh: useMovesVersion(leagueId) },
  );
}

/**
 * Keep a finished mock. Its `key` is the run's, so sending it again after an
 * answer that never arrived is the same mock, never a second.
 */
export function keepMock(
  leagueId: string,
  record: SaveMockRequest,
): Promise<Reply<SavedMockSummary>> {
  return thenRefresh(leagueId, send('POST', base(leagueId), record, SavedMockSummary));
}

/** Share one of mine to the league's chat, once. */
export function shareMock(leagueId: string, mockId: string): Promise<Reply<SavedMockSummary>> {
  return thenRefresh(
    leagueId,
    send(
      'POST',
      `${base(leagueId)}/${encodeURIComponent(mockId)}/share`,
      undefined,
      SavedMockSummary,
    ),
  );
}

/** Delete one of mine: my list without it. */
export function deleteMock(leagueId: string, mockId: string): Promise<Reply<SavedMocksView>> {
  return thenRefresh(
    leagueId,
    send('DELETE', `${base(leagueId)}/${encodeURIComponent(mockId)}`, undefined, SavedMocksView),
  );
}
